import {useDispatch, useSelector} from 'react-redux';
import {useCallback, useRef} from 'react';
import {useDebouncedCallback} from 'use-debounce';
import {selectedSlideSelector} from '../../../store/selectors/slide.selectors';
import {selectedPresentationSlidePreviewsSelector} from '../../../store/selectors/presentation.selectors';
import {swapArrayElement} from '../../utils';
import {ISlidePreview} from '../../../incase-domain/proptypes/slidePreview';
import {updateSelectedSlidePreviewsAction} from '../../../store/actions/slide.preview';
import {uploadSlidePreview} from '../../../service/slide.preview';
import {nodeToBase64} from '../../nodeToImage/nodeToBase64';
import {slideToImageFilter} from '../../nodeToImage/filters';
import {appendSlidePreviewCacheBust, stripSlidePreviewCacheBustForPersistence} from '../../slidePreviewUrl';
import {uploadSlidePreviewFile} from '../../../service/files.service';
import {dataUrlToFile} from '../../fileUtils';
import {createIdleScheduler} from '../../scheduleWhenIdle';
import {deferPreviewUntilDragEnds, isSlideElementDragging} from '../../slideDragGuard';

const IDLE_TIMEOUT_MS = 3000;

export const useSlidePreview = (): {
    updateSlidePreview: () => void;
    saveSlidePreview: () => void;
} => {
    const dispatch = useDispatch();
    const slide = useSelector(selectedSlideSelector);
    const selectedPresentationSlidePreviews = useSelector(selectedPresentationSlidePreviewsSelector);

    const slideWorkspace = document.getElementById('slide-workspace') as HTMLElement;
    const isPreviewRunningRef = useRef(false);
    const idleSchedulerRef = useRef(createIdleScheduler(IDLE_TIMEOUT_MS));
    const updateSlidePreviewRef = useRef<() => void>(() => undefined);

    const runPreviewCapture = useCallback((): void => {
        if (!slideWorkspace) return;
        if (isPreviewRunningRef.current) {
            updateSlidePreviewRef.current();
            return;
        }
        if (isSlideElementDragging()) {
            deferPreviewUntilDragEnds(() => idleSchedulerRef.current.schedule(runPreviewCapture));
            return;
        }

        isPreviewRunningRef.current = true;
        nodeToBase64(slideWorkspace, slideToImageFilter)
            .then(async (base64) => {
                if (!base64?.length) return;

                let imgSrc = base64;
                if (!slide._id) {
                    return;
                }
                try {
                    const file = dataUrlToFile(base64, `slide-preview-${slide._id}.png`);
                    const {url} = await uploadSlidePreviewFile(file, slide._id);
                    imgSrc = appendSlidePreviewCacheBust(url, Date.now());
                } catch {
                    return;
                }

                const slidePreview: ISlidePreview = {slideId: slide._id, imgSrc};

                const updatesSlidePreviewIndex = selectedPresentationSlidePreviews.findIndex(
                    (preview) => preview.slideId === slidePreview.slideId
                );

                let updatedSlidePreviewList: ISlidePreview[];
                if (updatesSlidePreviewIndex >= 0)
                    updatedSlidePreviewList = swapArrayElement(selectedPresentationSlidePreviews, updatesSlidePreviewIndex, slidePreview);
                else updatedSlidePreviewList = [...selectedPresentationSlidePreviews, slidePreview];

                dispatch(updateSelectedSlidePreviewsAction(updatedSlidePreviewList));
            })
            .finally(() => {
                isPreviewRunningRef.current = false;
            });
    }, [dispatch, selectedPresentationSlidePreviews, slide._id, slideWorkspace]);

    const updateSlidePreview = useDebouncedCallback((): void => {
        if (!slideWorkspace) return;

        idleSchedulerRef.current.schedule(runPreviewCapture);
    }, 2000);

    updateSlidePreviewRef.current = updateSlidePreview;

    const saveSlidePreview = (): void => {
        const slidePreview = selectedPresentationSlidePreviews.find((preview) => preview.slideId === slide._id);
        if (slidePreview) {
            uploadSlidePreview({
                ...slidePreview,
                imgSrc: stripSlidePreviewCacheBustForPersistence(slidePreview.imgSrc),
            });
        }
    };

    return {updateSlidePreview, saveSlidePreview};
};
